import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Coins, CheckCircle, Clock } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function EarnCreditsCard() {
  const { user, checkAuth } = useAuth();
  const { t } = useTranslation();
  const [bonuses, setBonuses] = useState({
    profile_bonus: { available: false, claimed: false, complete: false },
    transaction_bonus: { available: false, claimed: false, completed_transactions: 0 }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBonuses();
  }, [user]);

  const fetchBonuses = async () => {
    try {
      const response = await axios.get(`${API}/credits/check-bonuses`, { withCredentials: true });
      setBonuses(response.data);
    } catch (error) {
      console.error('Error fetching bonuses:', error);
    }
  };

  const claimProfileBonus = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/credits/claim-profile-bonus`, {}, { withCredentials: true });
      toast.success(`🎉 ${response.data.message}`, {
        description: `+${response.data.credits_earned} credit! New balance: ${response.data.new_balance}`
      });
      await checkAuth(); // Refresh user data
      await fetchBonuses(); // Refresh bonuses
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to claim bonus';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Don't show card if all bonuses are claimed
  if (bonuses.profile_bonus.claimed && bonuses.transaction_bonus.claimed) {
    return null;
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 border-2 border-slate-200 shadow-md">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative p-3 bg-gradient-to-br from-slate-300 to-slate-400 rounded-full shadow-lg">
          <Clock className="h-6 w-6 text-white" />
          <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1">
            <Coins className="h-3 w-3 text-yellow-900" />
          </div>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">{t('earnCredits.title')}</h3>
          <p className="text-sm text-slate-600">{t('earnCredits.subtitle')}</p>
        </div>
      </div>

      <div className="space-y-3">
        {/* Profile Bonus */}
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border-2 border-slate-100 hover:border-slate-300 transition-all">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {bonuses.profile_bonus.claimed ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <Clock className="h-4 w-4 text-slate-400" />
              )}
              <p className="font-medium text-gray-900">{t('earnCredits.completeProfile')}</p>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {bonuses.profile_bonus.claimed 
                ? t('earnCredits.claimed')
                : bonuses.profile_bonus.complete 
                  ? t('earnCredits.readyToClaim')
                  : t('earnCredits.fillProfile')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded">+1</span>
            {bonuses.profile_bonus.available && !bonuses.profile_bonus.claimed && (
              <Button
                onClick={claimProfileBonus}
                disabled={loading}
                size="sm"
                className="bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white shadow-md"
              >
                {t('earnCredits.claim')}
              </Button>
            )}
          </div>
        </div>

        {/* First Transaction Bonus */}
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border-2 border-slate-100 hover:border-slate-300 transition-all">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {bonuses.transaction_bonus.claimed ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <Clock className="h-4 w-4 text-slate-400" />
              )}
              <p className="font-medium text-gray-900">{t('earnCredits.firstTransaction')}</p>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {bonuses.transaction_bonus.claimed 
                ? t('earnCredits.claimed')
                : bonuses.transaction_bonus.available
                  ? t('earnCredits.autoAwarded')
                  : t('earnCredits.completeTransaction')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded">+1</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
